import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Cpu, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { trainingApi, type TrainingRunJoin } from '../../services/api';

/**
 * A run task's record (EP-26 FE-5 ST-4): status, timing and cost from
 * `run:<task _id>` in the programme's runs store, its gate events and the
 * models it produced. Read live through the programme's read credential; a
 * slow or unreachable store is shown as such, never as an empty panel.
 */
const STATE_NOTE: Record<string, string> = {
  unreachable: 'The runs store did not answer in time.',
  no_record: 'No run record found for this task.',
  no_credentials: 'The programme read credential is not available on this server.',
  partial: 'Some linked records could not be read.',
  error: 'The runs store returned an error.',
};

function duration(a?: string | null, b?: string | null): string {
  if (!a || !b) return '';
  const s = (Date.parse(b) - Date.parse(a)) / 1000;
  if (!Number.isFinite(s) || s < 0) return '';
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h ? `${h} h ${m} min` : `${m} min`;
}

export default function RunPanel({ programId, taskId }: { programId: string; taskId: string }) {
  const [run, setRun] = useState<TrainingRunJoin | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    trainingApi.run(programId, taskId)
      .then((r) => { setRun(r); setError(''); })
      .catch((e) => setError(String(e?.message || e)))
      .finally(() => setLoading(false));
  }, [programId, taskId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-xs text-red-600">Run record: {error}</p>;
  if (!run) return <p className="text-xs text-gray-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Reading run record…</p>;

  const rec = run.record;
  const degraded = run.state !== 'ok';

  return (
    <div className={`rounded-lg border ${degraded ? 'border-amber-200 dark:border-amber-800' : 'border-gray-200 dark:border-gray-700'} p-3 space-y-2`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
          <Cpu className="w-4 h-4 text-indigo-600" />
          Run {rec?.status ? `· ${rec.status}` : ''}
        </div>
        <button type="button" onClick={load} disabled={loading} title="Re-read the run record"
          className="text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 flex items-center gap-1 disabled:opacity-50">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {degraded && (
        <div className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            {STATE_NOTE[run.state] || run.state}
            {run.messages.map((m, i) => <span key={i} className="block text-gray-600 dark:text-gray-400">{m}</span>)}
          </span>
        </div>
      )}

      <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-gray-500">Record</dt><dd className="text-gray-900 dark:text-gray-100"><code>{run.runId}</code></dd>
        {rec && (<>
          <dt className="text-gray-500">Stage</dt><dd className="text-gray-900 dark:text-gray-100">{rec.stage || '—'} {rec.purpose ? `(${rec.purpose})` : ''}</dd>
          <dt className="text-gray-500">Started</dt><dd className="text-gray-900 dark:text-gray-100">{rec.started ? new Date(rec.started).toLocaleString() : 'not yet'}</dd>
          {rec.ended ? (<><dt className="text-gray-500">Ended</dt><dd className="text-gray-900 dark:text-gray-100">{new Date(rec.ended).toLocaleString()} {duration(rec.started, rec.ended) ? `· ${duration(rec.started, rec.ended)}` : ''}</dd></>) : null}
          <dt className="text-gray-500">Cost</dt><dd className="text-gray-900 dark:text-gray-100">{run.cost != null ? `$${Number(run.cost).toFixed(2)}` : '—'}</dd>
        </>)}
      </dl>

      {run.gateEvents.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Gate events</div>
          <ul className="space-y-1">
            {run.gateEvents.map((g) => (
              <li key={g.id} className="text-xs text-gray-800 dark:text-gray-200 flex items-start gap-1.5">
                {g.passed
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                  : <XCircle className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />}
                <span><strong>{g.gate || g.id}</strong>{g.reason ? <span className="text-gray-500"> · {g.reason}</span> : null}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {run.models.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Models produced</div>
          <ul className="space-y-1">
            {run.models.map((m) => (
              <li key={m.id} className="text-xs text-gray-800 dark:text-gray-200">
                <code>{m.id}</code>{m.base_model ? ` · ${m.base_model}` : ''}{m.seed != null ? ` · seed ${m.seed}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
